import { useRef, useState } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { Plus } from "lucide-react";

// Objection-handling FAQ (HP2 only) — last-mile CRO. Answers the questions
// that stall a sensitive-skin purchase, placed just above the Final CTA.
const FAQS = [
  {
    q: "Will it work for sensitive skin, rosacea or redness?",
    a: "Yes — Riversol was created by dermatologist Dr. Jason Rivers specifically for reactive, sensitive skin. Every formula is fragrance-free, hypoallergenic and non-comedogenic, and clinically developed to calm visible redness rather than trigger it.",
  },
  {
    q: "How soon will I see results?",
    a: "Most people notice calmer, more hydrated skin within the first two weeks. Visible improvements in dark spots, redness and texture typically build over 4–8 weeks of consistent daily use.",
  },
  {
    q: "What if it doesn't work for me?",
    a: "You're covered by our 60-day money-back guarantee. If you're not seeing the results you hoped for, contact us within 60 days for a full refund — no questions asked.",
  },
  {
    q: "Is it fragrance-free and cruelty-free?",
    a: "Every product is 100% fragrance-free, vegan and cruelty-free, and proudly made in Canada. No added fragrance, dyes or harsh irritants.",
  },
  {
    q: "How do I know which products to start with?",
    a: "Take the 60-second Skin Analyzer or start with the four-step routine (Preparation, Treatment, Hydration, Protection). You can also build a bundle and save up to 25%.",
  },
];

const Item = ({ item, index, isOpen, onToggle }) => (
  <div className="border-b border-neutral-200" data-testid={`faq-item-${index}`}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      data-testid={`faq-toggle-${index}`}
      className="group w-full flex items-start justify-between gap-6 py-6 lg:py-7 text-left"
    >
      <span className="text-[17px] lg:text-[21px] leading-snug tracking-[-0.01em] text-neutral-900">
        {item.q}
      </span>
      <span
        className={`shrink-0 mt-1 w-8 h-8 rounded-full border flex items-center justify-center transition-colors ${
          isOpen
            ? "bg-riversol-blue border-riversol-blue text-white"
            : "border-riversol-blue/30 text-riversol-blue group-hover:border-riversol-blue"
        }`}
      >
        <Plus size={15} strokeWidth={1.8} className={`transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`} />
      </span>
    </button>
    <AnimatePresence initial={false}>
      {isOpen && (
        <motion.div
          key="a"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="overflow-hidden"
        >
          <p className="pb-6 lg:pb-7 pr-12 text-[14px] lg:text-[15px] leading-relaxed text-neutral-600 max-w-[720px]">
            {item.a}
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

export const FaqSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const [open, setOpen] = useState(0);

  return (
    <section ref={ref} className="bg-white py-24 lg:py-32" data-testid="home-faq">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:sticky lg:top-28"
        >
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-500">
            Before you start
          </div>
          <h2 className="mt-4 text-[32px] sm:text-[38px] lg:text-[46px] leading-[1.05] tracking-[-0.02em] text-neutral-900">
            Questions, <span className="italic text-riversol-blue">answered.</span>
          </h2>
          <p className="mt-5 text-[14px] lg:text-[15px] text-neutral-500 leading-relaxed max-w-[22rem]">
            Everything you need to feel confident before trying Riversol. Still unsure? Our team is a message away.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.12, ease: [0.16, 1, 0.3, 1] }}
          className="border-t border-neutral-200"
        >
          {FAQS.map((item, i) => (
            <Item
              key={item.q}
              item={item}
              index={i}
              isOpen={open === i}
              onToggle={() => setOpen(open === i ? -1 : i)}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FaqSection;
